import React, { useState, useEffect, useContext } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import { useV2MonthYear } from "../../hooks/useV2MonthYear";
import { useLocation, useNavigate } from "react-router-dom";
import "./Page.css";
import "./ActionsList.css";
import MonthYearNavigator from "./MonthYearNavigator";
import CleanLogo from "../../img/v2/clean-logo.svg";
import LeafIcon from "../../img/v2/leaf-icon.svg";
import WaterIcon from "../../img/v2/water-icon.svg";
import {
  LoadingIndicator,
  MonetarySavingsChart,
  WaterSavingsChart,
  GreenMilesUsageChart,
  ResourceSavingsCard,
  GreenMilesDistributionCard,
  UsageStatisticsCard,
} from "./ActionsListComponents";

const ActionsList = () => {
  const keycloak = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [stats, setStats] = useState(null);

  const { month: selectedMonth, year: selectedYear, setMonthYear } = useV2MonthYear();

  const fetchStats = () => {
    if (!keycloak.isAuthenticated || !keycloak.user?.access_token || !hId) {
      setHasError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    const params = new URLSearchParams({
      lang,
    });
    if (selectedMonth) params.append("month", String(selectedMonth));
    if (selectedYear) params.append("year", String(selectedYear));

    const url = `/api/hotels/${hId}/actions-list-stats?${params.toString()}`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errData) => {
            throw new Error(errData.message || `HTTP error! status: ${response.status}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data.success === "OK") {
          setStats(data.data);
          setHasError(false);
        } else {
          setHasError(true);
          console.error("Error fetching stats:", data.message || "Unknown error");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setHasError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (keycloak.isAuthenticated && keycloak.user && hId) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.isAuthenticated, hId, selectedMonth, selectedYear, lang]);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "--";
    return typeof num === "number" ? num.toLocaleString(locale, { maximumFractionDigits: 2 }) : num;
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return "--";
    return typeof num === "number"
      ? `${num.toLocaleString(locale, { maximumFractionDigits: 2 })} €`
      : num;
  };

  const formatWater = (num) => {
    if (num === null || num === undefined) return "--";
    return typeof num === "number"
      ? `${num.toLocaleString(locale, { maximumFractionDigits: 2 })} L`
      : num;
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return "--";
    return typeof num === "number" ? `${Math.round(num)}%` : num;
  };

  const getActionIcon = (type) => {
    const typeUpper = type?.toUpperCase();
    switch (typeUpper) {
      case "LUNICLEAN":
        return <img src={CleanLogo} alt="LuniClean" className="usage-statistics__icon" />;
      case "LUNISHOWER":
        return <img src={WaterIcon} alt="LuniShower" className="usage-statistics__icon" />;
      default:
        return <img src={LeafIcon} alt="Action" className="usage-statistics__icon usage-statistics__icon--small" />;
    }
  };

  const navigateByActionType = (actionType) => {
    const typeUpper = actionType?.toUpperCase?.() || "";
    const basePath = "/hotel/lunishower/v2";

    let pageKey = null;
    if (typeUpper === "LUNICLEAN" || typeUpper === "YESNOMAIL") pageKey = "actions-cleaning";
    else if (typeUpper === "LUNISHOWER") pageKey = "actions-water";
    else if (typeUpper === "QUIZ") pageKey = "actions-surveys";
    else if (typeUpper === "REVIEW") pageKey = "actions-reviews";

    if (!pageKey) return;
    navigate(`${basePath}/${pageKey}${location.search}`);
  };

  return (
    <div className="v2-page">
      <div className="v2-page__header">
        <h1>{translate("v2_actions_list_title", lang)}</h1>
        <MonthYearNavigator
          month={selectedMonth}
          year={selectedYear}
          onChange={({ month, year }) => {
            setMonthYear({ month, year });
          }}
        />
      </div>

      {hasError && (
        <div style={{ padding: "16px", background: "#ffebee", color: "#c62828", borderRadius: "4px", marginBottom: "24px" }}>
          {translate("v2_error_load_data", lang)}
        </div>
      )}
      <div className="actions-list__content">
        <div className="actions-list__stats-top">
          <div className="actions-list__stat-card">
            <div className="monetary-card__header">
              <h3 className="monetary-card__title">{translate("v2_actions_list_monetary", lang)}</h3>
              <p className="monetary-card__subtitle">{translate("v2_actions_list_by_type", lang)}</p>
            </div>
            {loading ? (
              <LoadingIndicator />
            ) : (
              <MonetarySavingsChart data={stats?.monetarySavings} formatCurrency={formatCurrency} />
            )}
          </div>

          <div className="actions-list__stat-card">
            <div className="monetary-card__header">
              <h3 className="monetary-card__title">{translate("v2_actions_list_water", lang)}</h3>
              <p className="monetary-card__subtitle">{translate("v2_actions_list_by_type", lang)}</p>
            </div>
            {loading ? (
              <LoadingIndicator />
            ) : (
              <WaterSavingsChart data={stats?.waterSavings} formatWater={formatWater} />
            )}
          </div>

          <div className="actions-list__stat-card">
            <div className="monetary-card__header">
              <h3 className="monetary-card__title">{translate("v2_actions_list_gm_usage", lang)}</h3>
              <p className="monetary-card__subtitle">{translate("v2_actions_list_stays_action", lang)}</p>
            </div>
            {loading ? (
              <LoadingIndicator />
            ) : (
              <GreenMilesUsageChart
                percentage={stats?.greenMilesUsage?.percentage}
                formatPercentage={formatPercentage}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="actions-list__card actions-list__card-large">
            <div className="monetary-card__header">
              <h3 className="monetary-card__title">{translate("v2_actions_list_usage_stats", lang)}</h3>
              <p className="monetary-card__subtitle">{translate("v2_actions_list_usage_trends", lang)}</p>
            </div>
            <LoadingIndicator />
          </div>
        ) : (
          <UsageStatisticsCard
            actions={stats?.usageStatistics?.actions || []}
            formatNumber={formatNumber}
            formatCurrency={formatCurrency}
            formatPercentage={formatPercentage}
            getActionIcon={getActionIcon}
            onActionNavigate={(action) => navigateByActionType(action?.type)}
          />
        )}

        <div className="actions-list__stats-bottom">
          {loading ? (
            <div className="actions-list__card">
              <div className="monetary-card__header">
                <h3 className="monetary-card__title">{translate("v2_actions_list_resource_savings", lang)}</h3>
                <p className="monetary-card__subtitle">{translate("v2_actions_list_pollution", lang)}</p>
              </div>
              <LoadingIndicator />
            </div>
          ) : (
            <ResourceSavingsCard
              actions={stats?.resourceSavings?.actions || []}
              formatNumber={formatNumber}
              getActionIcon={getActionIcon}
            />
          )}

          {loading ? (
            <div className="actions-list__card">
              <div className="monetary-card__header">
                <h3 className="monetary-card__title">{translate("v2_actions_list_gm_distribution", lang)}</h3>
                <p className="monetary-card__subtitle">{translate("v2_actions_list_by_type", lang)}</p>
              </div>
              <LoadingIndicator />
            </div>
          ) : (
            <GreenMilesDistributionCard
              actions={stats?.greenMilesDistribution?.actions || []}
              formatNumber={formatNumber}
              getActionIcon={getActionIcon}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionsList;
